// /groups/:groupId/manage — settings, roles, invites, and invite codes.
// Admin (100) and above. The server enforces the same gate on every call.

import { HEADER_REFRESH_EVENT } from './headerBell.js';
import {
  ApiClientError,
  createInviteCode,
  deleteGroup,
  fetchGroup,
  inviteMember,
  listInviteCodes,
  listMembers,
  requestCategory,
  setInviteCodeActive,
  transferOwnership,
  updateGroup,
  updateMemberRole,
  uploadGroupPhoto,
  removeGroupPhoto,
  listJoinRequests,
  approveJoinRequest,
  denyJoinRequest,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import { eventTypePicker } from './eventTypePicker.js';
import { tzPicker } from './tzPicker.js';
import { inviteSection } from './inviteMemberForm.js';
import { roleSelect } from './roleSelect.js';
import {
  GRANTABLE_ROLES,
  GroupRole,
  groupRoleLabel,
  INVITE_PERMISSION_CHOICES,
  invitePermissionHint,
  invitePermissionLabel,
  POST_PERMISSION_CHOICES,
  postPermissionLabel,
  type GroupDetail,
  type GroupMember,
  fmtInstant,
  TimeFormat,
  type InviteCode,
  type InvitePermission,
  type PostPermission,
  GROUP_VISIBILITIES,
  GROUP_JOIN_POLICIES,
  groupVisibilityLabel,
  groupJoinPolicyLabel,
  type GroupVisibility,
  type GroupJoinPolicy,
  type GroupJoinRequest,
} from '@msc/shared';

export const groupManageView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  let members: GroupMember[];
  try {
    [group, members] = await Promise.all([fetchGroup(groupId), listMembers(groupId)]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    const msg =
      err instanceof ApiClientError && err.status === 403
        ? 'You need to be a group admin to manage this group.'
        : 'Could not load this group.';
    main.replaceChildren(el('p', { class: 'form-error' }, msg), backLink(groupId));
    return;
  }

  main.replaceChildren(
    backLink(groupId),
    // Reachable by definition (the fetch above 403s anyone under ADMIN), so the
    // tab is always shown active here.
    groupSubnav(groupId, 'manage', true, groupRunsFasts(group.allowedEventTypes), groupShowsLeaderboard(group.allowedEventTypes)),
    el('h1', {}, `Manage ${group.name}`),
    // ⚠ Its own section, OUTSIDE the settings form, and not cosmetic: the
    // settings form submits JSON through PATCH while the photo is a separate
    // multipart endpoint. A file input inside that form would look like part of
    // the same Save and silently do nothing when Save was pressed. This saves on
    // its own, immediately, so it carries its own status line too.
    // People (roster, roles, invites, join requests, codes) live on the Members
    // tab since 2026-10-20; this page is group SETTINGS only.
    el('p', { class: 'muted small' }, [
      'Looking for roles, invites or join requests? They are on the ',
      el('a', { href: `/groups/${groupId}/members` }, 'Members tab'),
      '.',
    ]),
    photoSection(group),
    settingsSection(group),
    dangerSection(group, members),
  );
};

function backLink(groupId: number): HTMLElement {
  return el('p', {}, [el('a', { href: `/groups/${groupId}` }, 'Back to the group')]);
}

// --- Join requests --------------------------------------------------------

/**
 * The pending-request queue, for an owner or admin.
 *
 * Renders NOTHING when the queue is empty AND the group is private -- there is
 * nothing to decide and no way for anything to arrive, so a permanently empty
 * box would just be furniture. It DOES render an empty state for a public
 * group, because "no requests yet" is real information there.
 *
 * ⚠ Both decisions return the REMAINING queue, and this re-renders from that
 * rather than removing the row locally. Two admins work the same queue -- the
 * server's answer is the only thing that knows what the other one just did.
 */
export function joinRequestsSection(group: GroupDetail): HTMLElement {
  const section = el('section', { class: 'card' });
  const error = el('p', { class: 'form-error', hidden: true });
  const list = el('div', {});

  /**
   * The count beside the heading.
   *
   * ⚠ A LIVE NODE that `paint()` rewrites, not a value read once from the group
   * payload. It used to be the latter, so approving emptied the list and left
   * the badge insisting one request was still waiting -- which is the specific
   * cosmetic failure that makes somebody press Approve a second time looking
   * for it.
   */
  const badge = el('span', { class: 'badge-count', hidden: true });

  function setBadge(n: number): void {
    badge.textContent = String(n);
    badge.hidden = n === 0;
  }
  setBadge(group.pendingJoinRequests);

  function paint(requests: GroupJoinRequest[]): void {
    setBadge(requests.length);
    // The HEADER's chip counts the same thing from a different endpoint, so it
    // is just as stale. Telling it to re-read is what stops the two counters
    // disagreeing after a decision.
    document.dispatchEvent(new CustomEvent(HEADER_REFRESH_EVENT));
    if (!requests.length) {
      list.replaceChildren(
        el(
          'p',
          { class: 'muted small' },
          group.visibility === 'public'
            ? 'Nobody is waiting. Requests appear here when someone asks to join.'
            : 'This group is private, so nobody can ask to join it.',
        ),
      );
      return;
    }
    list.replaceChildren(
      ...requests.map((r) => {
        const approve = el('button', { type: 'button', class: 'btn btn-sm' }, 'Approve');
        const deny = el('button', { type: 'button', class: 'btn btn-sm btn-link btn-danger' }, 'Deny');

        async function decide(fn: () => Promise<GroupJoinRequest[]>): Promise<void> {
          error.hidden = true;
          approve.setAttribute('disabled', 'disabled');
          deny.setAttribute('disabled', 'disabled');
          try {
            paint(await fn());
          } catch (err) {
            error.textContent =
              err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
            error.hidden = false;
            // Re-enabled only on failure. On success this node is gone, and
            // re-enabling a detached button is how a second click gets sent.
            approve.removeAttribute('disabled');
            deny.removeAttribute('disabled');
          }
        }

        approve.addEventListener('click', () =>
          decide(() => approveJoinRequest(group.groupId, r.requestId)),
        );
        deny.addEventListener('click', () =>
          decide(() => denyJoinRequest(group.groupId, r.requestId)),
        );

        return el('div', { class: 'joinreq' }, [
          el('div', { class: 'joinreq-who' }, [
            // ⚠ The name LINKS to the profile; the email is deliberately gone.
            // On a public group anyone signed in can ask, so printing the
            // address handed a stranger's contact detail to an owner before
            // any relationship existed -- and a denied request disclosed it
            // too. The approver still needs to place the person, and the
            // profile page is where that belongs: projectProfile() is the one
            // place deciding what a viewer may see, and it carries no email.
            el('a', { class: 'joinreq-name', href: `/users/${r.userId}` }, r.fullName),
            ...(r.message ? [el('p', { class: 'joinreq-msg' }, r.message)] : []),
          ]),
          el('div', { class: 'joinreq-actions' }, [approve, deny]),
        ]);
      }),
    );
  }

  // Painted from the count on the group payload first, so the section is
  // correct before the fetch lands, then replaced by the real rows.
  paint([]);
  if (group.pendingJoinRequests > 0) {
    list.replaceChildren(el('p', { class: 'muted small' }, 'Loading…'));
  }
  void listJoinRequests(group.groupId)
    .then(paint)
    .catch(() => {
      error.textContent = 'Could not load the requests.';
      error.hidden = false;
      paint([]);
    });

  section.replaceChildren(
    // ⚠ `id` so the header chip can anchor HERE. The chip links to
    // /groups/<id>/manage, which opens on Group photo with this queue about two
    // screens down past settings, event types and the invite form -- the count
    // is the notification, so it has to land on the thing it counted.
    el('h2', { id: 'join-requests' }, ['Join requests', badge]),
    error,
    list,
  );
  return section;
}

// --- Group photo ----------------------------------------------------------

/**
 * The group photo control: preview, choose, remove.
 *
 * Uploads on CHOOSE rather than behind its own Upload button. One less press,
 * and the file input is already an explicit act -- there is no way to pick a
 * file by accident. The preview is what confirms it worked.
 */
function photoSection(group: GroupDetail): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const ok = el('p', { class: 'form-ok', hidden: true });

  // `accept` matches what the server's sniffer actually allows. It is a HINT to
  // the file picker, never a check -- the server sniffs magic bytes, and a
  // renamed file passes this and is refused there.
  const input = el('input', {
    id: 'groupPhoto',
    type: 'file',
    accept: 'image/jpeg,image/png,image/webp,image/gif',
  }) as HTMLInputElement;

  const remove = el(
    'button',
    { type: 'button', class: 'btn btn-link btn-danger gphoto-remove' },
    'Remove photo',
  );

  const preview = el('div', { class: 'ginfo-photo gphoto-preview' });

  function paint(url: string): void {
    if (url) {
      preview.replaceChildren(el('img', { src: url, alt: group.name }));
      remove.hidden = false;
    } else {
      preview.replaceChildren(el('p', { class: 'muted small' }, 'No photo yet.'));
      remove.hidden = true;
    }
  }
  paint(group.photoUrl);

  function busy(on: boolean): void {
    // Both controls, not just the one pressed: a remove landing while an upload
    // is in flight would race the two writes against each other.
    if (on) {
      input.setAttribute('disabled', 'disabled');
      remove.setAttribute('disabled', 'disabled');
    } else {
      input.removeAttribute('disabled');
      remove.removeAttribute('disabled');
    }
  }

  function failed(err: unknown): void {
    error.textContent =
      err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
    error.hidden = false;
  }

  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    if (!file) return;
    error.hidden = true;
    ok.hidden = true;
    busy(true);
    try {
      const fresh = await uploadGroupPhoto(group.groupId, file);
      paint(fresh.photoUrl);
      ok.textContent = 'Photo updated.';
      ok.hidden = false;
    } catch (err) {
      failed(err);
    } finally {
      busy(false);
      // ⚠ Cleared either way. Without this, choosing the SAME file again fires
      // no `change` event, so a retry after a failure looks like a dead control.
      input.value = '';
    }
  });

  remove.addEventListener('click', async () => {
    error.hidden = true;
    ok.hidden = true;
    busy(true);
    try {
      const fresh = await removeGroupPhoto(group.groupId);
      paint(fresh.photoUrl);
      ok.textContent = 'Photo removed.';
      ok.hidden = false;
    } catch (err) {
      failed(err);
    } finally {
      busy(false);
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Group photo'),
    el(
      'p',
      { class: 'muted small' },
      'Shown at the top of the group info panel. Up to 10 MB; it is resized for you.',
    ),
    preview,
    el('label', { for: 'groupPhoto' }, 'Choose a photo'),
    input,
    remove,
    error,
    ok,
  ]);
}

// --- Settings -------------------------------------------------------------

function settingsSection(group: GroupDetail): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const ok = el('p', { class: 'form-ok', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Save settings');

  // autocorrect off, for the reason spelled out on the create form in groups.ts.
  const name = el('input', {
    id: 'name',
    name: 'name',
    type: 'text',
    required: true,
    maxlength: 191,
    autocorrect: 'off',
    spellcheck: 'false',
    value: group.name,
  }) as HTMLInputElement;

  const description = el('textarea', { id: 'description', name: 'description', rows: 3 }) as HTMLTextAreaElement;
  description.value = group.description;

  // The group info panel's three text fields. maxlength MIRRORS the server's
  // zod caps (255 / 40), so the browser stops an over-long value before a
  // round trip -- the server still enforces it, this is only courtesy.
  const about = el('input', {
    id: 'about',
    name: 'about',
    type: 'text',
    maxlength: 255,
    placeholder: 'Poker every Thursday, cash game, low stakes',
    value: group.about,
  }) as HTMLInputElement;

  // ⚠ type=date, so the browser hands back exactly 'YYYY-MM-DD' -- the same
  // naive shape the column stores and the server validates. Do NOT be tempted
  // into a datetime-local here: this is the month a group began, not an
  // instant, and anything carrying a zone shifts the day (CLAUDE.md).
  const established = el('input', {
    id: 'establishedOn',
    name: 'establishedOn',
    type: 'date',
    value: group.establishedOn ?? '',
  }) as HTMLInputElement;

  const memberNoun = el('input', {
    id: 'memberNoun',
    name: 'memberNoun',
    type: 'text',
    maxlength: 40,
    placeholder: 'members',
    // Autocorrect off for the same reason the group NAME has it off: a typed
    // proper noun like "Card Sharks" gets helpfully mangled on the iOS shell.
    autocorrect: 'off',
    spellcheck: 'false',
    value: group.memberNoun === 'members' ? '' : group.memberNoun,
  }) as HTMLInputElement;

  // Choices and labels come from @msc/shared so this form and the create-group
  // modal cannot drift apart again — they already had, saying "Any member can
  // post" here and "Any member" there.
  const permission = el('select', { id: 'postPermission', name: 'postPermission' }) as HTMLSelectElement;
  for (const value of POST_PERMISSION_CHOICES) {
    const opt = el('option', { value }, postPermissionLabel(value)) as HTMLOptionElement;
    if (value === group.postPermission) opt.selected = true;
    permission.append(opt);
  }

  // Who may invite. OWNER ONLY, the one field on this form with a gate of its
  // own: an Admin who could widen it could grant themselves the power to add
  // more Admins. An Admin therefore sees the current value as read-only text
  // rather than a disabled select, which reads as broken.
  //
  // The server refuses a non-owner sending a DIFFERENT value and treats absent
  // as unchanged, so the read-only branch simply sends nothing.
  const isOwner = group.myRole >= GroupRole.OWNER;
  const invitePerm = el('select', {
    id: 'invitePermission',
    name: 'invitePermission',
  }) as HTMLSelectElement;
  for (const value of INVITE_PERMISSION_CHOICES) {
    const opt = el('option', { value }, invitePermissionLabel(value)) as HTMLOptionElement;
    if (value === group.invitePermission) opt.selected = true;
    invitePerm.append(opt);
  }
  const inviteHint = el('p', { class: 'muted small' }, invitePermissionHint(group.invitePermission));
  invitePerm.addEventListener('change', () => {
    inviteHint.textContent = invitePermissionHint(invitePerm.value as InvitePermission);
  });

  const inviteRows: HTMLElement[] = isOwner
    ? [el('label', { for: 'invitePermission' }, 'Who can invite new members?'), invitePerm, inviteHint]
    : [
        el('label', {}, 'Who can invite new members?'),
        el('p', {}, invitePermissionLabel(group.invitePermission)),
        el('p', { class: 'muted small' }, 'Only the group owner can change this.'),
      ];

  // Null means open, and the picker checks everything for it — that is what
  // NULL has always meant and the form must show it honestly.
  const eventTypes = eventTypePicker({
    selected: group.allowedEventTypes,
    idPrefix: 'gm',
  });

  const suggest = suggestCategoryLink(group.groupId);

  const approval = el('input', {
    id: 'requireApproval',
    name: 'requireApproval',
    type: 'checkbox',
  }) as HTMLInputElement;
  approval.checked = group.requireApproval;

  // Was a free-text input, where an inexact string is a hard 400 Unknown
  // timezone. The picker only ever submits a canonical id or '' for the site
  // default. See docs/migration-gaps.md item 4.
  // ⚠ OWNER ONLY, and omitted entirely for an Admin rather than shown disabled.
  // The server refuses a CHANGE from a non-owner; rendering the controls would
  // invite an admin to try and be told no. Same treatment as invitePermission,
  // whose rows are built the same way just above.
  const visibility = el('select', { id: 'visibility', name: 'visibility' }) as HTMLSelectElement;
  for (const v of GROUP_VISIBILITIES) {
    visibility.append(el('option', { value: v }, groupVisibilityLabel(v)) as HTMLOptionElement);
  }
  visibility.value = group.visibility;

  const joinPolicy = el('select', { id: 'joinPolicy', name: 'joinPolicy' }) as HTMLSelectElement;
  for (const jp of GROUP_JOIN_POLICIES) {
    joinPolicy.append(el('option', { value: jp }, groupJoinPolicyLabel(jp)) as HTMLOptionElement);
  }
  joinPolicy.value = group.joinPolicy;

  // The join policy only means anything for a public group. Hidden rather than
  // disabled while private, because a disabled control still reads as a setting
  // that applies -- and the value is deliberately still SAVED either way, so
  // flipping to public does not lose the owner's earlier choice.
  const joinPolicyRow = el('div', { class: 'field-row' }, [
    el('label', { for: 'joinPolicy' }, 'When someone asks to join'),
    joinPolicy,
  ]);
  function syncJoinPolicyRow(): void {
    joinPolicyRow.hidden = visibility.value !== 'public';
  }
  visibility.addEventListener('change', syncJoinPolicyRow);
  syncJoinPolicyRow();

  const discoveryRows: HTMLElement[] = isOwner
    ? [
        el('label', { for: 'visibility' }, 'Who can find this group?'),
        visibility,
        el(
          'p',
          { class: 'muted small' },
          'Public only affects who can FIND the group. The feed, chat, photos and ' +
            'events always stay members-only.',
        ),
        joinPolicyRow,
      ]
    : [];

  const timeZone = tzPicker({
    name: 'timeZone',
    value: group.timeZone ?? '',
    inheritLabel: 'Site default',
    id: 'timeZone',
  });

  const form = el('form', { method: 'post', action: `/groups/${group.groupId}` }, [
    el('label', { for: 'name' }, 'Group name'),
    name,
    el('label', { for: 'description' }, 'Description'),
    description,
    el('label', { for: 'about' }, "We're about"),
    about,
    el(
      'p',
      { class: 'muted small' },
      'One line for the group info panel, shorter than the description above it.',
    ),
    el('label', { for: 'establishedOn' }, 'Established'),
    established,
    el(
      'p',
      { class: 'muted small' },
      'When the group itself began, which may be long before it was added here. Shown as a month and year.',
    ),
    el('label', { for: 'memberNoun' }, 'What do you call your members?'),
    memberNoun,
    el(
      'p',
      { class: 'muted small' },
      'Plural, so the panel can read "11 Card Sharks". Leave blank for "members".',
    ),
    el('label', {}, 'Which event types does this group offer?'),
    eventTypes.element,
    el(
      'p',
      { class: 'muted small' },
      'Plain events are always available. Narrowing or opening back up never touches existing events.',
    ),
    suggest,
    el('label', { for: 'postPermission' }, 'Who can post photos & content?'),
    permission,
    ...inviteRows,
    el('label', { class: 'check', for: 'requireApproval' }, [
      approval,
      ' Require approval before photos go live',
    ]),
    ...discoveryRows,
    el('label', { for: 'timeZone' }, 'Default event timezone'),
    timeZone.root,
    el(
      'p',
      { class: 'muted small' },
      'This is only the default offered when creating an event. Each event stores its own zone, so changing this never shifts existing events.',
    ),
    error,
    ok,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    ok.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      await updateGroup(group.groupId, {
        name: name.value.trim(),
        description: description.value.trim() || undefined,
        postPermission: permission.value as PostPermission,
        // Omitted entirely for a non-owner. The server reads absent as
        // "unchanged" -- sending the current value back would work too, but
        // omitting it means a non-owner request can never be refused for a
        // field they never touched.
        ...(isOwner ? { invitePermission: invitePerm.value as InvitePermission } : {}),
        requireApproval: approval.checked,
        // ⚠ ALWAYS SENT, even when blank. PATCH /groups/:id is a FULL-ROW
        // overwrite, not a partial patch -- `updateGroup()` writes every column
        // it knows about, and an absent field lands as NULL exactly like an
        // empty one (measured, not assumed: omitting `about` after setting it
        // clears it, same as `description` has always behaved). So this form
        // must resubmit its whole current state, which it does. Sending '' is
        // therefore not what makes a field clearable -- it is what makes the
        // intent obvious at the call site.
        about: about.value.trim(),
        establishedOn: established.value,
        memberNoun: memberNoun.value.trim(),
        // Sent only by an OWNER. An Admin omits them, and the server reads
        // absent as "unchanged" for these two specifically -- so an admin
        // saving this form is never refused for a field they cannot see.
        ...(isOwner
          ? {
              visibility: visibility.value as GroupVisibility,
              joinPolicy: joinPolicy.value as GroupJoinPolicy,
            }
          : {}),
        // '' means the site default; the API treats an absent zone the same way.
        timeZone: timeZone.value() || undefined,
        allowedEventTypes: eventTypes.value(),
      });
      ok.textContent = 'Saved.';
      ok.hidden = false;
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Settings'),
    el('p', { class: 'muted small' }, `URL slug: ${group.slug} (fixed, so links keep working)`),
    form,
  ]);
}

/**
 * "Suggest a new event type" — pure signal collection for a site admin to
 * review; adding one for real still takes a code change either way. See
 * docs/domain.md, "Group categories".
 */
function suggestCategoryLink(groupId: number): HTMLElement {
  const toggle = el('button', { type: 'button', class: 'btn-link small' }, "Don't see the right event type? Suggest one");
  const nameInput = el('input', {
    type: 'text',
    maxlength: 100,
    placeholder: 'e.g. Mahjong',
  }) as HTMLInputElement;
  const descInput = el('textarea', { rows: 2, placeholder: 'What would this need? (optional)' }) as HTMLTextAreaElement;
  const submit = el('button', { type: 'button', class: 'btn btn-secondary btn-sm' }, 'Submit suggestion');
  const status = el('p', { class: 'muted small', hidden: true });

  // No class here deliberately: `.form-group`'s `display: flex` has the same
  // CSS specificity as the `[hidden]` UA rule and, being an author rule,
  // wins the tie — so pairing that class with `hidden` silently un-hides it.
  const panel = el('div', { hidden: true }, [nameInput, descInput, submit, status]);

  toggle.addEventListener('click', () => {
    panel.hidden = !panel.hidden;
  });

  submit.addEventListener('click', async () => {
    const trimmedName = nameInput.value.trim();
    if (!trimmedName) {
      status.textContent = 'Give it a name first.';
      status.hidden = false;
      return;
    }
    submit.setAttribute('disabled', 'disabled');
    try {
      await requestCategory({
        name: trimmedName,
        description: descInput.value.trim() || undefined,
        groupId,
      });
      status.textContent = 'Thanks. Sent to the site admins.';
      status.hidden = false;
      nameInput.value = '';
      descInput.value = '';
    } catch {
      status.textContent = 'Could not send that. Try again.';
      status.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  });

  return el('div', { class: 'form-group' }, [toggle, panel]);
}

// --- Roles ----------------------------------------------------------------

function memberLabel(m: GroupMember): string {
  return m.fullName || m.email || `User ${m.userId}`;
}

/**
 * The ownership-transfer control, or null when there is nothing to offer.
 *
 * Only the OWNER may transfer (the server agrees: requireGroupRole(OWNER) on the
 * route). Absent in a group of one: there is nobody to transfer to, and an empty
 * picker beside a live button is worse than no picker. It lives in the Danger zone
 * at the bottom of Manage since 2026-10-20, no longer inside the Roles table.
 */
function transferControls(group: GroupDetail, members: GroupMember[]): HTMLElement | null {
  const owner = members.find((m) => m.role >= GroupRole.OWNER);
  if (!owner || group.myRole < GroupRole.OWNER) return null;
  const others = members.filter((m) => m.userId !== owner.userId);
  if (others.length === 0) return null;

  const pick = el('select', { id: 'transfer-to', name: 'userId' }) as HTMLSelectElement;
  pick.append(el('option', { value: '' }, 'Choose a member'));
  for (const m of others) {
    pick.append(
      el(
        'option',
        { value: String(m.userId) },
        `${memberLabel(m)} (${groupRoleLabel(m.role)})`,
      ) as HTMLOptionElement,
    );
  }

  const status = el('span', { class: 'muted small' }, '');
  const start = el('button', { type: 'button', class: 'btn-link' }, 'Transfer ownership');

  // Two deliberate clicks on two DIFFERENT labels, and the second one names the
  // person. Not a window.confirm() and not one button: this hands control of
  // other people's data to somebody else and the caller cannot undo it -- only
  // the new owner can hand it back. A single red button one stray click away from
  // that is the accident worth designing out. Matches the reasoning behind the
  // typed count on the contacts bulk delete, scaled to a one-person action.
  const confirmBtn = el('button', { type: 'button', class: 'btn btn-danger' }, '');
  const cancelBtn = el('button', { type: 'button', class: 'btn-link' }, 'Cancel');
  const confirmRow = el('span', { class: 'transfer-confirm', hidden: true }, [
    confirmBtn,
    ' ',
    cancelBtn,
  ]);

  function reset(): void {
    confirmRow.hidden = true;
    start.hidden = false;
    pick.removeAttribute('disabled');
  }

  start.addEventListener('click', () => {
    const chosen = others.find((m) => String(m.userId) === pick.value);
    if (!chosen) {
      status.textContent = 'Choose a member first.';
      return;
    }
    status.textContent = '';
    confirmBtn.textContent = `Yes, make ${memberLabel(chosen)} the owner`;
    start.hidden = true;
    confirmRow.hidden = false;
    pick.setAttribute('disabled', 'disabled');
    confirmBtn.focus();
  });

  cancelBtn.addEventListener('click', reset);

  confirmBtn.addEventListener('click', async () => {
    const chosen = others.find((m) => String(m.userId) === pick.value);
    if (!chosen) {
      reset();
      return;
    }
    confirmBtn.setAttribute('disabled', 'disabled');
    status.textContent = 'Transferring';
    try {
      const result = await transferOwnership(group.groupId, chosen.userId);
      // The caller's own role just changed, and this page renders differently for
      // an admin than for an owner -- the delete-group section included. Re-render
      // rather than patching the row, or the screen keeps offering owner-only
      // controls the server will now refuse.
      status.textContent = `${memberLabel(chosen)} is now the owner. You are ${result.previousOwnerRoleLabel}.`;
      navigate(`/groups/${group.groupId}/manage`, true);
    } catch (err) {
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not transfer ownership.';
      confirmBtn.removeAttribute('disabled');
      reset();
    }
  });

  return el('div', { class: 'transfer-owner' }, [
    el('label', { for: 'transfer-to', class: 'small' }, 'Hand this group to'),
    pick,
    ' ',
    start,
    confirmRow,
    el('div', {}, status),
    el('p', { class: 'muted small' }, 'They become Owner and you become Admin. Only they can transfer it back.'),
  ]);
}

// --- Invite codes ---------------------------------------------------------

export function codesSection(group: GroupDetail, codes: InviteCode[]): HTMLElement {
  const list = el('tbody', {}, codes.map((c) => codeRow(group.groupId, c)));

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Create code');

  const maxUses = el('input', {
    id: 'code-max',
    name: 'maxUses',
    type: 'number',
    min: 1,
    placeholder: 'Unlimited',
  }) as HTMLInputElement;

  const expires = el('input', { id: 'code-expires', name: 'expires', type: 'date' }) as HTMLInputElement;
  const note = el('input', {
    id: 'code-note',
    name: 'note',
    type: 'text',
    maxlength: 191,
    placeholder: 'Optional label',
  }) as HTMLInputElement;

  const form = el('form', { method: 'post', action: `/groups/${group.groupId}/invite-codes` }, [
    el('label', { for: 'code-max' }, 'Max uses'),
    maxUses,
    el('label', { for: 'code-expires' }, 'Expires'),
    expires,
    el('label', { for: 'code-note' }, 'Note'),
    note,
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const code = await createInviteCode(group.groupId, {
        maxUses: maxUses.value ? Number(maxUses.value) : undefined,
        expires: expires.value || undefined,
        note: note.value.trim() || undefined,
      });
      list.prepend(codeRow(group.groupId, code));
      maxUses.value = '';
      expires.value = '';
      note.value = '';
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Invite codes'),
    el(
      'p',
      { class: 'muted small' },
      'Anyone signing up with one of these codes joins this group automatically. Codes are always scoped to this group.',
    ),
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'table' }, [
        el('thead', {}, [
          el('tr', {}, ['Code', 'Uses', 'Expires', 'Status', ''].map((h) => el('th', {}, h))),
        ]),
        list,
      ]),
    ]),
    form,
  ]);
}

/**
 * expiresAt is a UTC INSTANT, never a date, so it must not be rendered by
 * slicing the string. It is stored as the end of the chosen day in the SITE
 * zone, which lands on the following calendar day for a viewer east of the site
 * zone. Showing the full instant with its zone makes that legible instead of
 * looking like an off-by-one: an admin who typed Dec 31 sees the exact moment
 * the code stops working in their own zone.
 */
function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'Never';
  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return fmtInstant(expiresAt, viewerZone, TimeFormat.INSTANT);
}

function codeRow(groupId: number, code: InviteCode): HTMLElement {
  const status = el('td', {}, code.isActive ? 'Active' : 'Revoked');
  const toggle = el('button', { class: 'btn btn-link' }, code.isActive ? 'Revoke' : 'Restore');

  let active = code.isActive;
  toggle.addEventListener('click', async () => {
    toggle.setAttribute('disabled', 'disabled');
    try {
      const updated = await setInviteCodeActive(groupId, code.codeId, !active);
      active = updated.isActive;
      status.textContent = active ? 'Active' : 'Revoked';
      toggle.textContent = active ? 'Revoke' : 'Restore';
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not update that code.');
    } finally {
      toggle.removeAttribute('disabled');
    }
  });

  return el('tr', {}, [
    el('td', {}, el('code', {}, code.code)),
    el('td', {}, code.maxUses === null ? `${code.useCount} of unlimited` : `${code.useCount} of ${code.maxUses}`),
    el('td', {}, formatExpiry(code.expiresAt)),
    status,
    el('td', {}, toggle),
  ]);
}

// --- Danger zone ----------------------------------------------------------

function dangerSection(group: GroupDetail, members: GroupMember[]): HTMLElement {
  // Owner only. An admin cannot delete the group itself, and the server agrees.
  if (group.myRole < GroupRole.OWNER) {
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Delete group'),
      el('p', { class: 'muted' }, 'Only the group owner can delete this group.'),
    ]);
  }

  const btn = el('button', { class: 'btn btn-danger btn-standalone' }, 'Delete this group');
  btn.addEventListener('click', async () => {
    if (!window.confirm(`Delete ${group.name}? Members lose access immediately.`)) return;
    btn.setAttribute('disabled', 'disabled');
    try {
      await deleteGroup(group.groupId);
      navigate('/groups', true);
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not delete the group.');
      btn.removeAttribute('disabled');
    }
  });

  const transfer = transferControls(group, members);
  return el('section', { class: 'card' }, [
    el('details', {}, [
      el('summary', {}, 'Danger zone: transfer ownership or delete this group'),
      ...(transfer ? [el('h3', {}, 'Transfer ownership'), transfer] : []),
      el('h3', {}, 'Delete group'),
      el('p', { class: 'muted' }, 'The group is hidden from everyone. Events, photos, and results are kept.'),
      btn,
    ]),
  ]);
}
